import { Card, CardContent, Typography, Box, Chip, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#FFFFFF',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.4s ease',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: theme.palette.accent.gradient,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',

    '&::after': {
      opacity: 1,
    },

    '& .product-icon': {
      transform: 'scale(1.08)',
    },

    '& .product-overlay': {
      opacity: 1,
    },
  },
}));

const ImageWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'gradient' })(({ theme, gradient }) => ({
  position: 'relative',
  height: '200px',
  overflow: 'hidden',
  background: gradient || 'linear-gradient(135deg, rgba(0, 184, 212, 0.08) 0%, rgba(91, 141, 239, 0.15) 50%, rgba(0, 184, 212, 0.08) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    height: '180px',
    padding: theme.spacing(3),
  },
}));

const ProductIcon = styled('img')({
  maxWidth: '75%',
  maxHeight: '85%',
  objectFit: 'contain',
  transition: 'transform 0.4s ease',
  position: 'relative',
  zIndex: 1,
});

const ImageOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  zIndex: 2,
}));

const FallbackContent = styled(Box)({
  position: 'relative',
  zIndex: 1,
  textAlign: 'center',
});

// Gradient backgrounds for each product (similar to hero style)
const productBackgrounds = {
  deenmatches: 'linear-gradient(135deg, rgba(13, 148, 136, 0.08) 0%, rgba(20, 184, 166, 0.15) 50%, rgba(13, 148, 136, 0.08) 100%)',
  bookmoteasy: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08) 0%, rgba(234, 88, 12, 0.15) 50%, rgba(249, 115, 22, 0.08) 100%)',
  crux360: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(29, 78, 216, 0.15) 50%, rgba(59, 130, 246, 0.08) 100%)',
  arimmigration: 'linear-gradient(135deg, rgba(1, 33, 105, 0.08) 0%, rgba(200, 16, 46, 0.12) 50%, rgba(1, 33, 105, 0.08) 100%)',
  birminghamsleepcentre: 'linear-gradient(135deg, rgba(33, 38, 85, 0.08) 0%, rgba(243, 199, 123, 0.20) 50%, rgba(33, 38, 85, 0.08) 100%)',
};

// Accent colors for fallback text
const productColors = {
  deenmatches: '#0d9488',
  bookmoteasy: '#f97316',
  crux360: '#3b82f6',
  arimmigration: '#012169',
  birminghamsleepcentre: '#212655',
};

export default function ProductCard({
  productKey,
  title,
  description,
  features = [],
  url,
  image,
}) {
  const { t } = useTranslation();
  const gradient = productBackgrounds[productKey] || 'linear-gradient(135deg, rgba(0, 184, 212, 0.08) 0%, rgba(91, 141, 239, 0.15) 50%, rgba(0, 184, 212, 0.08) 100%)';
  const accentColor = productColors[productKey] || '#0ea5e9';

  const handleCardClick = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <StyledCard onClick={handleCardClick}>
      <ImageWrapper gradient={gradient}>
        {image ? (
          <ProductIcon
            src={image}
            alt={title}
            className="product-icon"
            loading="lazy"
          />
        ) : (
          <FallbackContent>
            <Typography
              variant="h4"
              sx={{
                color: accentColor,
                fontWeight: 700,
              }}
            >
              {title}
            </Typography>
          </FallbackContent>
        )}
        <ImageOverlay className="product-overlay">
          <Button
            variant="contained"
            size="small"
            endIcon={<OpenInNewIcon sx={{ fontSize: '16px' }} />}
            sx={{
              background: '#FFFFFF',
              color: 'text.primary',
              fontWeight: 600,
              '&:hover': {
                background: '#FFFFFF',
              },
            }}
          >
            {t('products.visitWebsite')}
          </Button>
        </ImageOverlay>
      </ImageWrapper>

      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2.5,
            lineHeight: 1.7,
          }}
        >
          {description}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {features.map((feature, index) => (
            <Chip
              key={index}
              label={feature}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(91, 141, 239, 0.08) 100%)',
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '0.7rem',
                height: '24px',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </StyledCard>
  );
}
